import { CmcWebApi } from '@cmctechnology/webinvest-api-client-web-trading';
import { configureStore, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  AccountNo,
  CorporateAction,
  CurrencyCode,
  FxRateKey,
  IExchangeInstrumentQuote,
  IFxRate,
  IInstrumentExtendedTrading,
  IInstrumentQuote,
  IInstrumentStatus,
  InstrumentCode,
  PriceMovement,
  QuoteKey,
  TradingState
} from './types';
import { toLastPriceMovementFromQuoteContainerDtoV1, toLastPriceMovementFromQuoteModel } from './utils';
import {
  MARKETDATA_WS_STORE_NAME,
  createMarketDataWsStore,
  defaultMarketDataWsStore,
  marketDataWsActions,
  ExchangeId,
  QuoteResponseWrapper,
  QuoteContainerDtoV1
} from '@cmctechnology/webinvest-api-client-market-data-ws';
import { CLIENT_STORE_NAME, createClientStore, defaultClientStore } from '@cmctechnology/webinvest-store-client';
import isEqual from 'lodash.isequal';
import { createFrontendStore, defaultFrontendStore, FRONTEND_STORE_NAME } from '@cmctechnology/webinvest-store-frontend';

export const MARKET_DATA_STORE_NAME = `marketData`;
export type FxRates = Record<AccountNo, Record<CurrencyCode, Record<CurrencyCode, IFxRate>>>;

export interface IMarketDataStore {
  quotes: Record<InstrumentCode, IInstrumentQuote>;
  screener: {
    upgrades: CmcWebApi.MarketInformation.TheScreenerStockFullModel1[];
    downgrades: CmcWebApi.MarketInformation.TheScreenerStockFullModel1[];
  };
  morningstar: {
    underValuedStocks: CmcWebApi.MarketInformation.MorningstarQuantitativeModel2[];
    overValuedStocks: CmcWebApi.MarketInformation.MorningstarQuantitativeModel2[];
  };
  fxRates: FxRates;
  fxRateSubscriptions: FxRateKey[];
  quoteSubscriptions: QuoteKey[];
}

export const defaultMarketDataStore: IMarketDataStore = {
  quotes: {},
  screener: {
    upgrades: [],
    downgrades: []
  },
  morningstar: {
    underValuedStocks: [],
    overValuedStocks: []
  },
  fxRates: {},
  fxRateSubscriptions: [],
  quoteSubscriptions: []
};

const invertFxRate = (fxRate: { fromCurrencyCode: string; toCurrencyCode: string; bid: number; offer: number }) => ({
  fromCurrencyCode: fxRate.toCurrencyCode,
  toCurrencyCode: fxRate.fromCurrencyCode,
  bid: fxRate.bid === 0 ? 0 : 1 / fxRate.offer,
  offer: fxRate.offer === 0 ? 0 : 1 / fxRate.bid
});

const updateQuote = (quote: IInstrumentQuote, quoteV1: QuoteContainerDtoV1, exchangeId: string) => {
  const lastQuote = quote.exchanges[exchangeId];
  const lastUpdatedApi = lastQuote?.lastUpdatedApi;

  let extendedTrading: IInstrumentExtendedTrading | undefined = undefined;
  if (quoteV1.extendedTrading) {
    switch (quoteV1.basic?.tradingState) {
      case TradingState.PreTrade: {
        extendedTrading = {
          lastPrice: quoteV1.basic?.last! + quoteV1.extendedTrading.preTradeNetChange!,
          priceChange: quoteV1.extendedTrading.preTradeNetChange,
          priceChangePercent: quoteV1.extendedTrading.preTradeNetChange! / quoteV1.basic?.last!,
          totalVolumeTraded: quoteV1.extendedTrading.preTradeVolume
        } as IInstrumentExtendedTrading;
        break;
      }
      case TradingState.PostTrade: {
        extendedTrading = {
          lastPrice: quoteV1.basic?.last! + quoteV1.extendedTrading.postTradeNetChange!,
          priceChange: quoteV1.extendedTrading.postTradeNetChange,
          priceChangePercent: quoteV1.extendedTrading.postTradeNetChange! / quoteV1.basic?.last!,
          totalVolumeTraded: quoteV1.extendedTrading.postTradeVolume
        } as IInstrumentExtendedTrading;
      }
    }
  }

  const lastPriceMovement = toLastPriceMovementFromQuoteContainerDtoV1(lastQuote, quoteV1, extendedTrading?.lastPrice);
  const open = quoteV1.quote?.open ?? 0;
  const close = quoteV1.quote?.close ?? 0;
  const last = quoteV1.basic?.last ?? 0;
  const displayPrice = last === 0 ? close : last;
  const priceChange = last === 0 ? open - close : last - close;
  const priceChangePercent = close === 0 ? 0 : priceChange / close;

  // Commented out props below are in QuoteBatch API but not sent by WS,
  // kept for documentation purposes
  // eslint-disable-next-line no-param-reassign
  quote.exchanges[exchangeId] = {
    hash: undefined as unknown as string,
    // bestBuyPrice: quoteModel.BestBuyPrice,
    // bestSellPrice: quoteModel.BestSellPrice,
    bids: quoteV1.quote?.bids!,
    bidsTotalVolume: quoteV1.quote?.bidsTotalVolume!,
    // brokerageScript: quoteModel.BrokerageScript,
    closingPrice: close,
    corporateActions:
      /* istanbul ignore next */
      quoteV1.quote?.corporateActions?.map((corporateAction) => ({
        code: corporateAction.code as CorporateAction,
        description: corporateAction.description!,
        name: corporateAction.name!
      })) ?? [],
    country: quoteV1.instrumentData?.countryIsoCode ?? lastQuote?.country,
    currency: quoteV1.instrumentData?.currencyCode ?? lastQuote?.currency,
    decimalPrecisionForDisplay: quoteV1.instrumentData?.decimalPrecisionForDisplay ?? lastQuote?.decimalPrecisionForDisplay,
    delayed: quoteV1.delayed!,
    displayPrice,
    // displayPriceType: quoteModel.DisplayPriceType,
    extendedTrading,
    // hasValidPricing: quoteModel.HasValidPricing,
    highSalePrice: quoteV1.quote?.high!,
    // indicativePrice: quoteModel.IndicativePrice,
    // isTradeable: quoteModel.IsTradeable,
    lastPrice: last,
    lastPriceMovement,
    lastYield: quoteV1.quote?.lastYield!,
    lowSalePrice: quoteV1.quote?.low!,
    matchVolume: quoteV1.quote?.matchVolume!,
    midPrice: quoteV1.quote?.mid!,
    offers: quoteV1.quote?.offers!,
    offersTotalVolume: quoteV1.quote?.offersTotalVolume!,
    // openInterest: quoteModel.OpenInterest,
    openingPrice: open,
    priceChange,
    priceChangePercent,
    quoteTime: Date.parse(quoteV1.createdUtc!).valueOf(),
    status: {
      code: quoteV1.basic?.tradingState! as TradingState
      // description: quoteModel.StockStatus.Description,
      // name: quoteModel.StockStatus.Name
    } as IInstrumentStatus,
    surplusVolume: quoteV1.quote?.surplusVolume!,
    totalNumberOfTrades: quoteV1.quote?.totalNumberOfTrades!,
    totalValueTraded: quoteV1.quote?.totalValueTraded!,
    totalVolumeTraded: quoteV1.quote?.totalVolumeTraded!,
    vwapPrice: quoteV1.quote?.vWap!,
    lastUpdated: Date.now().valueOf(),
    lastUpdatedApi
  } as IExchangeInstrumentQuote;
};

const makeQuote = (quoteModel: CmcWebApi.MarketInformation.QuoteModel2, lastPriceMovement: PriceMovement) => ({
  hash: quoteModel.Hash,
  bestBuyPrice: quoteModel.BestBuyPrice,
  bestSellPrice: quoteModel.BestSellPrice,
  bids: quoteModel.Bids,
  bidsTotalVolume: quoteModel.BidsTotalVolume,
  brokerageScript: quoteModel.BrokerageScript,
  closingPrice: quoteModel.ClosingPrice,
  corporateActions: quoteModel.CorporateActions?.map((corporateAction) => ({
    code: corporateAction.Code as CorporateAction,
    description: corporateAction.Description,
    name: corporateAction.Name
  })),
  country: quoteModel.CountryIsoCode,
  currency: quoteModel.CurrencyCode,
  decimalPrecisionForDisplay: quoteModel.DecimalPrecisionForDisplay,
  delayed: quoteModel.Delayed,
  displayPrice: quoteModel.DisplayPrice,
  displayPriceType: quoteModel.DisplayPriceType,
  extendedTrading: quoteModel.ExtendedTrading && {
    lastPrice: quoteModel.ExtendedTrading.LastPrice,
    priceChange: quoteModel.ExtendedTrading.PriceChange,
    priceChangePercent: quoteModel.ExtendedTrading.PriceChangePercent,
    totalVolumeTraded: quoteModel.ExtendedTrading.TotalVolumeTraded
  },
  hasValidPricing: quoteModel.HasValidPricing,
  highSalePrice: quoteModel.HighSalePrice,
  indicativePrice: quoteModel.IndicativePrice,
  isTradeable: quoteModel.IsTradeable,
  lastPrice: quoteModel.LastPrice,
  lastPriceMovement,
  lastYield: quoteModel.LastYield,
  lowSalePrice: quoteModel.LowSalePrice,
  matchVolume: quoteModel.MatchVolume,
  midPrice: quoteModel.MidPrice,
  offers: quoteModel.Offers,
  offersTotalVolume: quoteModel.OffersTotalVolume,
  openInterest: quoteModel.OpenInterest,
  openingPrice: quoteModel.OpeningPrice,
  priceChange: quoteModel.PriceChange,
  priceChangePercent: quoteModel.PriceChangePercent,
  quoteTime: 0,
  status: quoteModel.StockStatus && {
    code: quoteModel.StockStatus.Code as TradingState,
    description: quoteModel.StockStatus.Description,
    name: quoteModel.StockStatus.Name
  },
  surplusVolume: quoteModel.SurplusVolume,
  totalNumberOfTrades: quoteModel.TotalNumberOfTrades,
  totalValueTraded: quoteModel.TotalValueTraded,
  totalVolumeTraded: quoteModel.TotalVolumeTraded,
  vwapPrice: quoteModel.VWapPrice,
  lastUpdated: 0,
  lastUpdatedApi: 0
});

const makeFxRate = (fxRateModel: CmcWebApi.MarketInformation.ExchangeRateModel1) => ({
  bid: fxRateModel.Bid,
  offer: fxRateModel.Offer,
  unavailable: fxRateModel.Unavailable,
  error: fxRateModel.ErrorReason,
  lastUpdated: 0
});

export const createMarketDataStore = (initialState: IMarketDataStore) =>
  createSlice({
    name: MARKET_DATA_STORE_NAME,
    initialState,
    reducers: {
      storeReset: () => defaultMarketDataStore,

      quoteFetched: (store, action: PayloadAction<CmcWebApi.MarketInformation.QuoteModel2[]>) => {
        action.payload.forEach((quoteModel) => {
          const quote = (store.quotes[quoteModel.StockCode] ??= { code: quoteModel.StockCode, exchanges: {} });
          const lastPriceMovement = toLastPriceMovementFromQuoteModel(quote.exchanges[quoteModel.ExchangeId], quoteModel);

          const newQuote = makeQuote(quoteModel, lastPriceMovement);
          const oldQuote = quote.exchanges[quoteModel.ExchangeId];

          if (!isEqual(newQuote, oldQuote)) {
            quote.exchanges[quoteModel.ExchangeId] = newQuote;
          }
        });
      },

      screenerFetched: (store, action: PayloadAction<CmcWebApi.MarketInformation.TheScreenerUpgradeDowngradeModel1>) => {
        if (action.payload.Upgrades) {
          store.screener.upgrades = action.payload.Upgrades;
        }
        if (action.payload.Downgrades) {
          store.screener.downgrades = action.payload.Downgrades;
        }
      },
      morningstarFetched: (store, action: PayloadAction<CmcWebApi.MarketInformation.MorningstarQuantitativeSummaryModel2>) => {
        if (action.payload.UnderValuedStocks) {
          store.morningstar.underValuedStocks = action.payload.UnderValuedStocks;
        }
        if (action.payload.OverValuedStocks) {
          store.morningstar.overValuedStocks = action.payload.OverValuedStocks;
        }
      },
      fxRatesFetched: (store, action: PayloadAction<(FxRateKey & { fxRate: CmcWebApi.MarketInformation.ExchangeRateModel1 })[]>) => {
        action.payload.forEach((rate) => {
          const account = (store.fxRates[rate.accountNo] ??= {});
          const from = (account[rate.fromCurrencyCode] ??= {});

          const newFxRate = makeFxRate(rate.fxRate);
          const oldFxRate = from[rate.toCurrencyCode];

          if (!isEqual(newFxRate, oldFxRate)) {
            from[rate.toCurrencyCode] = newFxRate;
          }
        });
      },
      fxRatesSubscribed: (store, action: PayloadAction<FxRateKey[]>) => {
        store.fxRateSubscriptions = action.payload;
      },
      quotesSubscribed: (store, action: PayloadAction<QuoteKey[]>) => {
        store.quoteSubscriptions = action.payload;
      }
    },
    extraReducers: (builder) => {
      builder.addCase(marketDataWsActions.quoteV1MessageReceived, (store, action: PayloadAction<QuoteResponseWrapper>) => {
        if (action.payload?.quoteV1?.exchangeId === ExchangeId.Forex) {
          const accountNo = action.payload.account!;
          const account = (store.fxRates[accountNo] ??= {});
          let [fromCurrencyCode, toCurrencyCode] = action.payload?.quoteV1?.instrumentCode!.split(`/`);
          let fxRate = { fromCurrencyCode, toCurrencyCode, bid: action.payload?.quoteV1?.quote?.bid ?? 0, offer: action.payload?.quoteV1?.quote?.offer ?? 0 };
          if (!store.fxRateSubscriptions.some((sub) => isEqual(sub, { accountNo, fromCurrencyCode, toCurrencyCode }))) {
            fxRate = invertFxRate(fxRate);
          }
          const from = (account[fxRate.fromCurrencyCode] ??= {});
          from[fxRate.toCurrencyCode] = {
            bid: fxRate.bid,
            offer: fxRate.offer,
            unavailable: fxRate.bid === 0 || fxRate.offer === 0,
            error: action.payload?.error?.message,
            lastUpdated: Date.now().valueOf()
          };
        } else if (action.payload?.quoteV1?.instrumentCode && action.payload?.quoteV1?.exchangeId) {
          const instrumentCode = action.payload.quoteV1.instrumentCode;
          const exchangeId = action.payload.quoteV1.exchangeId;

          const quote = (store.quotes[instrumentCode] ??= { code: instrumentCode, exchanges: {} });
          updateQuote(quote, action.payload.quoteV1, exchangeId);
        }
      });
    }
  });

export const { actions, reducer } = createMarketDataStore(defaultMarketDataStore);

export const createStore = (initialMarketDataStore = defaultMarketDataStore) =>
  configureStore({
    reducer: {
      [MARKET_DATA_STORE_NAME]: createMarketDataStore(initialMarketDataStore).reducer,
      [FRONTEND_STORE_NAME]: createFrontendStore(defaultFrontendStore).reducer,
      [CLIENT_STORE_NAME]: createClientStore(defaultClientStore).reducer,
      [MARKETDATA_WS_STORE_NAME]: createMarketDataWsStore(defaultMarketDataWsStore).reducer
    }
  });

export const MarketDataStore = createStore();

export type IStore = ReturnType<typeof MarketDataStore.getState>;
